import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { interactionsService } from '../services/interactions.service';
import { employeesService } from '../services/employees.service';
import type {
  CreatePraiseApiRequest,
  CreateFeedbackApiRequest,
  CreatePraiseResponse,
  CreateFeedbackResponse,
  CreateDebriefResponse,
  UpdateDebriefResponse,
  UpdateFeedbackApiRequest,
  UpdateFeedbackResponse,
  CreateDebriefApiRequestUI,
  UpdateDebriefApiRequestUI,
  EnrichInteractionResponse,
  EnrichInteractionApiRequest,
  CloseInteractionResponse,
  UpdatePraiseApiRequest,
  UpdatePraiseResponse,
  UpdateActionPlanRequest,
  OnboardingApiRequest,
} from '@momentum/types';

// Query keys for interactions
export const interactionKeys = {
  all: ['interactions'] as const,
  lists: () => [...interactionKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) =>
    [...interactionKeys.lists(), { filters }] as const,
  details: () => [...interactionKeys.all, 'detail'] as const,
  detail: (id: string) => [...interactionKeys.details(), id] as const,
  praise: () => [...interactionKeys.all, 'praise'] as const,
  feedback: () => [...interactionKeys.all, 'feedback'] as const,
  debrief: () => [...interactionKeys.all, 'debrief'] as const,
  byGoalId: (goalId: string) =>
    [...interactionKeys.all, 'goal', goalId] as const,
  byIds: (ids: string[]) =>
    [...interactionKeys.all, 'ids', ids.sort().join(',')] as const,
  allMyInteractions: () =>
    [...interactionKeys.all, 'allMyInteractions'] as const,
  openBlockages: () => [...interactionKeys.all, 'openBlockages'] as const,
};

// Create praise interaction mutation
export const useCreatePraise = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePraiseApiRequest) =>
      interactionsService.createPraise(data),
    onSuccess: (newPraise: CreatePraiseResponse) => {
      // Invalidate and refetch interactions list
      queryClient.invalidateQueries({ queryKey: interactionKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: interactionKeys.allMyInteractions(),
      });

      // Optionally, you can set the new praise data in the cache
      queryClient.setQueryData(
        interactionKeys.detail(newPraise._id),
        newPraise
      );

      // Invalidate interactions for the goal if it exists
      if (newPraise.goal?.id) {
        queryClient.invalidateQueries({
          queryKey: interactionKeys.byGoalId(newPraise.goal.id),
        });
      }
    },
    onError: (error) => {
      console.error('Failed to create praise:', error);
    },
  });
};

// Update praise interaction mutation
export const useUpdatePraise = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePraiseApiRequest }) =>
      interactionsService.updatePraise(id, data),
    onSuccess: (updatedPraise: UpdatePraiseResponse) => {
      queryClient.invalidateQueries({
        queryKey: interactionKeys.detail(updatedPraise._id),
      });
    },
    onError: (error) => {
      console.error('Failed to create praise:', error);
    },
  });
};

export const useGetPraise = (id: string) => {
  return useQuery({
    queryKey: interactionKeys.detail(id),
    queryFn: () => interactionsService.getPraise(id),
    enabled: !!id,
  });
};

// Create feedback interaction mutation
export const useCreateFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFeedbackApiRequest) =>
      interactionsService.createFeedback(data),
    onSuccess: (newFeedback: CreateFeedbackResponse) => {
      // Invalidate and refetch interactions list
      queryClient.invalidateQueries({ queryKey: interactionKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: interactionKeys.allMyInteractions(),
      });
      queryClient.invalidateQueries({
        queryKey: interactionKeys.openBlockages(),
      });

      // Set the new feedback data in the cache
      queryClient.setQueryData(
        interactionKeys.detail(newFeedback._id),
        newFeedback
      );

      // Invalidate interactions for the goal if it exists
      if (newFeedback.goal?.id) {
        queryClient.invalidateQueries({
          queryKey: interactionKeys.byGoalId(newFeedback.goal.id),
        });
      }
    },
    onError: (error) => {
      console.error('Failed to create feedback:', error);
    },
  });
};

export const useUpdateFeedback = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateFeedbackApiRequest;
    }) => interactionsService.updateFeedback(id, data),
    onSuccess: (updatedFeedback: UpdateFeedbackResponse) => {
      queryClient.invalidateQueries({
        queryKey: interactionKeys.detail(updatedFeedback._id),
      });
    },
  });
};

export const useGetFeedback = (id?: string) => {
  return useQuery({
    queryKey: interactionKeys.detail(id ?? ''),
    queryFn: () => interactionsService.getFeedback(id as string),
    enabled: !!id,
  });
};

// Create debrief interaction mutation
export const useCreateDebrief = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDebriefApiRequestUI) =>
      interactionsService.createDebrief(data),
    onSuccess: (newDebrief: CreateDebriefResponse) => {
      // Invalidate and refetch interactions list
      queryClient.invalidateQueries({ queryKey: interactionKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: interactionKeys.allMyInteractions(),
      });

      // Invalidate the detail cache to force fetching populated data from backend
      queryClient.invalidateQueries({
        queryKey: interactionKeys.detail(newDebrief._id),
      });

      // Invalidate interactions for the goal if it exists
      if (newDebrief.goal?.id) {
        queryClient.invalidateQueries({
          queryKey: interactionKeys.byGoalId(newDebrief.goal.id),
        });
      }
    },
    onError: (error) => {
      console.error('Failed to create debrief:', error);
    },
  });
};

// Get debrief interaction mutation
export const useGetDebrief = (id?: string) => {
  return useQuery({
    queryKey: interactionKeys.detail(id ?? ''),
    queryFn: () => interactionsService.getDebrief(id as string),
    enabled: !!id,
  });
};

export const useUpdateDebrief = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateDebriefApiRequestUI;
    }) => interactionsService.updateDebrief(id, data),
    onSuccess: (updatedDebrief: UpdateDebriefResponse) => {
      queryClient.invalidateQueries({
        queryKey: interactionKeys.detail(updatedDebrief._id),
      });

      // Invalidate interactions for the goal if it exists
      if (updatedDebrief.goal?.id) {
        queryClient.invalidateQueries({
          queryKey: interactionKeys.byGoalId(updatedDebrief.goal.id),
        });
      }
    },
  });
};

// Get all interactions by goal ID
export const useInteractionsByGoalId = (goalId: string, enabled = true) => {
  return useQuery({
    queryKey: interactionKeys.byGoalId(goalId),
    queryFn: () => interactionsService.getInteractionsByGoalId(goalId),
    enabled: enabled && !!goalId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get interactions by IDs with populated data
export const useInteractionsByIds = (
  interactionIds: string[],
  enabled = true
) => {
  return useQuery({
    queryKey: interactionKeys.byIds(interactionIds),
    queryFn: () => interactionsService.getInteractionsByIds({ interactionIds }),
    enabled: enabled && interactionIds.length > 0,
  });
};

export const useGetAllMyInteractions = (limit?: number) => {
  return useQuery({
    queryKey: [...interactionKeys.allMyInteractions(), limit],
    queryFn: () => interactionsService.getAllMyInteractions({ limit }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useGetOpenBlockages = () => {
  return useQuery({
    queryKey: interactionKeys.openBlockages(),
    queryFn: () => interactionsService.getOpenBlockages(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Enhance feedback with AI mutation
export const useEnrichFeedback = () => {
  return useMutation<
    EnrichInteractionResponse,
    Error,
    EnrichInteractionApiRequest
  >({
    mutationFn: (data: EnrichInteractionApiRequest) =>
      interactionsService.enrichFeedback(data),
    onError: (error) => {
      console.error('Failed to enhance feedback:', error);
    },
  });
};

export const useCloseDebrief = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => interactionsService.closeDebrief(id),
    onSuccess: (closedDebrief: CloseInteractionResponse) => {
      queryClient.invalidateQueries({
        queryKey: interactionKeys.detail(closedDebrief._id),
      });
    },
  });
};

export const useUpdateActionPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateActionPlanRequest }) =>
      interactionsService.updateActionPlan(id, data),
    onSuccess: (closedDebrief: CloseInteractionResponse) => {
      queryClient.invalidateQueries({
        queryKey: interactionKeys.detail(closedDebrief._id),
      });
      queryClient.invalidateQueries({
        queryKey: interactionKeys.openBlockages(),
      });
    },
    onError: (error) => {
      console.error('Failed to create action plan:', error);
    },
  });
};

// Onboarding mutation
export const useOnboarding = (options?: {
  onSuccess?: () => void | Promise<void>;
  onError?: (error: Error) => void;
}) => {
  return useMutation({
    mutationFn: (data?: Partial<OnboardingApiRequest>) =>
      employeesService.onboarding(data),
    onSuccess: async () => {
      if (options?.onSuccess) {
        await options.onSuccess();
      }
    },
    onError: (error: Error) => {
      console.error('Failed to complete onboarding:', error);
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};
